import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogProps,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import "yet-another-react-lightbox/styles.css";
import "./workOrderSegmentDetails.css";
import WorkOrderQueueAPI from "../../../QueueBoard/api/workOrderApi";
import SegmentLabor from "./labor/SegmentLabor";
import { SegmentDetailDialogProps } from "../../types/workOrderSegmentTypes";

import SegmentMobileComments from "./mobile-comments/SegmentMobileComments";
import { useDialogContext } from "components/common/context/TrackMuiDialogContext";
import SegmentAssociatedChecklist from "./checklist/SegmentAssociatedChecklist";
import SegmentItemlist from "./items/segmentItemsList";
import SegmentAttachedImages from "./attachment/AttachImages";
import SegmentAttachments from "./attachment/AttachFiles";
import {
  accordionStyle,
  accordionSummaryStyle,
  accordionTitleStyle,
  counterChipStyle,
} from "./workOrderSegmentDetailsDialogStyles";

const WorkOrderSegmentDetails = (
  {
    currentSegmentDetails,
    dialogToOpen,
    closeSegmentDetailDialog,
    isReadOnly = false,
    invoiceDocumentIdentity,
  }: SegmentDetailDialogProps,
  props: DialogProps
) => {
  const [openImg, setOpenImg] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false); // State to track loading status
  const { open } = props;
  const { registerDialog, unregisterDialog } = useDialogContext();

  useEffect(() => {
    if (open) {
      registerDialog();
    } else {
      unregisterDialog();
    }

    return () => {
      if (open) unregisterDialog();
    };
  }, [open]);

  const getImageUrl = useCallback(async (attachmentIdentity) => {
    setIsLoading(true);
    try {
      const imageUrl =
        WorkOrderQueueAPI.getSegmentImageByIdentity(attachmentIdentity);
      setIsLoading(false);
      return imageUrl;
    } catch (error) {
      setIsLoading(false);
      return null;
    }
  }, []);

  // Function to prepare slides for the Lightbox dynamically
  const getSlides = useCallback(() => {
    return currentSegmentDetails.images.map((image, index) => ({
      src: images[index] || "", // Use the image URL if available
      attachmentIdentity: image.attachmentIdentity,
      height: 500,
      width: 500,
    }));
  }, [currentSegmentDetails.images, images]);
  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setOpenImg(true);
  };

  const [imageLoading, setImageLoading] = useState(false);
  useEffect(() => {
    //console.log(theme.palette.mode);
    if (openImg) {
      const fetchImage = async (index) => {
        setImageLoading(true);
        const attachmentIdentity =
          currentSegmentDetails.images[index].attachmentIdentity;
        const imageUrl = await getImageUrl(attachmentIdentity);
        if (imageUrl) {
          setImages((prevImages) => {
            const newImages = [...prevImages];
            newImages[index] = imageUrl; // Set the fetched image URL at the correct index
            return newImages;
          });
        }
        setImageLoading(false);
      };

      fetchImage(currentImageIndex); // Fetch the image for the initially displayed slide
    }
  }, [openImg, currentImageIndex, currentSegmentDetails.images, getImageUrl]);
  const theme = useTheme();
  return (
    <Dialog
      open={dialogToOpen}
      fullWidth
      {...props}
      maxWidth="md"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          m: 0,
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : "#181F2A",
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.contrastText
              : "#fff",
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
        id="alert-dialog-title"
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            sx={{ marginLeft: "5px" }}
            variant="h6"
            fontSize="16px"
            fontWeight={500}
          >
            Segment Details
          </Typography>

          <IconButton
            sx={{ color: "white" }}
            onClick={() => closeSegmentDetailDialog(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        className="custom-scrollbar dialogContent"
        sx={{
          pt: 3,
        }}
      >
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "medium",
                mb: 2,
                borderBottom: "2px solid",
                borderColor: "primary.light",
                pb: 1,
              }}
            >
              {currentSegmentDetails.name}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              {/* Refactored fields rendering */}
              {(() => {
                const fields = [
                  { label: "Memo", value: currentSegmentDetails.memo },
                  {
                    label: "Standard Job Steps",
                    value: currentSegmentDetails.standardJobSteps,
                  },
                  // Add more fields here if needed
                ];
                const visibleFields = fields.filter(
                  (f) => f.value && f.value.length > 0
                );
                return visibleFields.map((field, idx) => (
                  <Box key={field.label} sx={{ mt: idx === 0 ? 0 : 2 }}>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        letterSpacing: 0,
                        fontWeight: 500,
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        mt: 0.5,
                        pl: 1,
                        borderLeft: "3px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      {field.value}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
          </Box>
        </Box>

        <Box sx={{ marginTop: "5px" }}>
          {/* Images Section */}
          {/* <Accordion sx={accordionStyle}>
            <AccordionSummary
              sx={accordionSummaryStyle}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography gutterBottom sx={accordionTitleStyle}>
                Images
                <Chip
                  variant="outlined"
                  label={currentSegmentDetails.images.length}
                  size="small"
                  sx={counterChipStyle}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails> */}

          <SegmentAttachments
            segmentIdentity={currentSegmentDetails.segmentIdentity}
            attachments={currentSegmentDetails.attachments}
            isReadOnly={isReadOnly}
            invoiceDocumentIdentity={invoiceDocumentIdentity}
          />
          <SegmentAttachedImages
            segmentIdentity={currentSegmentDetails.segmentIdentity}
            images={currentSegmentDetails.images}
            isReadOnly={isReadOnly}
          />
          {/* </AccordionDetails>
          </Accordion> */}

          {/* Comments Section */}

          <SegmentMobileComments
            segmentIdentity={currentSegmentDetails.segmentIdentity}
            comments={currentSegmentDetails.comments}
            isReadOnly={isReadOnly}
            invoiceDocumentIdentity={invoiceDocumentIdentity}
          />

          {/* Labor Section */}
          <Accordion
            sx={{
              ...accordionStyle,
              backgroundColor: (theme) =>
                theme.palette.mode === "light" ? "#fff" : "#181F2A",
            }}
          >
            <AccordionSummary
              sx={accordionSummaryStyle}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography gutterBottom sx={accordionTitleStyle}>
                Labor
                <Chip
                  variant="outlined"
                  label={`${currentSegmentDetails.labor
                    .reduce((acc, item) => acc + item.elapsedHours, 0)
                    .toFixed(2)}Hrs`}
                  size="small"
                  sx={counterChipStyle}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SegmentLabor labor={currentSegmentDetails.labor} />
            </AccordionDetails>
          </Accordion>

          {/* Checklist Section */}
          <Accordion
            sx={{
              ...accordionStyle,
              backgroundColor: (theme) =>
                theme.palette.mode === "light" ? "#fff" : "#181F2A",
            }}
          >
            <AccordionSummary
              sx={accordionSummaryStyle}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography gutterBottom sx={accordionTitleStyle}>
                Checklist
                <Chip
                  variant="outlined"
                  label={currentSegmentDetails.checklists.length}
                  size="small"
                  sx={counterChipStyle}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SegmentAssociatedChecklist
                checkListCollection={currentSegmentDetails.checklists}
                segmentIdentity={currentSegmentDetails.segmentIdentity}
                segmentName={currentSegmentDetails.name}
                workOrderNumber={currentSegmentDetails.workOrderNumber}
                isReadOnly={isReadOnly}
                invoiceDocumentIdentity={invoiceDocumentIdentity}
                onChecklistAdded={(newChecklist) => {
                  // Update the current segment details with the new checklist
                  // This would typically trigger a refresh of the data
                  // For now, we'll just add it to the local state
                  if (currentSegmentDetails.checklists) {
                    currentSegmentDetails.checklists.push(newChecklist);
                  }
                }}
              />
            </AccordionDetails>
          </Accordion>

          {/* Parts Section */}
          <Accordion
            sx={{
              ...accordionStyle,
              backgroundColor: (theme) =>
                theme.palette.mode === "light" ? "#fff" : "#181F2A",
            }}
          >
            <AccordionSummary
              sx={accordionSummaryStyle}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography gutterBottom sx={accordionTitleStyle}>
                Segment Items
                <Chip
                  variant="outlined"
                  label={currentSegmentDetails.items.length}
                  size="small"
                  sx={counterChipStyle}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SegmentItemlist invoiceDetails={currentSegmentDetails.items} />
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderSegmentDetails;
